import {createPlugin, PluginDef} from '@fullcalendar/core';
import {NamedTimeZoneImpl} from '@fullcalendar/core/internal';
import dayjs from 'dayjs/esm';
import utc from 'dayjs/esm/plugin/utc';
import timezone from 'dayjs/esm/plugin/timezone';

// Self-contained init so the plugin works regardless of import order. Safe to
// call repeatedly (dayjs.extend no-ops on the same plugin).
dayjs.extend(utc);
dayjs.extend(timezone);

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * FullCalendar named-timezone implementation backed by dayjs.
 *
 * FullCalendar 6 only understands `'local'` and `'UTC'` out of the box; a named
 * IANA zone (e.g. `Africa/Johannesburg`) needs a `NamedTimeZoneImpl`. The
 * official ones pull in luxon or moment-timezone — this repo standardises on
 * dayjs, so we provide our own thin adapter and add zero dependencies.
 *
 * FullCalendar talks to us with two primitives:
 *  - `offsetForArray(a)`: `a` is a wall-clock `[year, month(0-based), day, hour,
 *    minute, second, ms]` in this zone; return the UTC offset in **minutes**.
 *  - `timestampToArray(ms)`: given a UTC epoch-ms instant, return its wall-clock
 *    `[year, month(0-based), day, hour, minute, second, ms]` in this zone.
 */
export class DayjsNamedTimeZone extends NamedTimeZoneImpl {
  offsetForArray(a: number[]): number {
    const iso = `${a[0]}-${pad(a[1] + 1)}-${pad(a[2])}T${pad(a[3])}:${pad(a[4])}:${pad(a[5] ?? 0)}`;
    return dayjs.tz(iso, this.timeZoneName).utcOffset();
  }

  timestampToArray(ms: number): number[] {
    const d = dayjs(ms).tz(this.timeZoneName);
    return [d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second(), d.millisecond()];
  }
}

/**
 * Register this plugin and set `timeZone: '<IANA zone>'` on the calendar so the
 * grid, the now-indicator, and the dates returned by select/click/drag/resize
 * are all anchored to that zone instead of the viewer's device zone.
 */
export const dayjsTimeZonePlugin: PluginDef = createPlugin({
  name: 'xmedical-dayjs-timezone',
  namedTimeZonedImpl: DayjsNamedTimeZone,
});
